import { IThread } from '@/types';
import { formatDistanceToNow } from 'date-fns';
import { FC } from 'react';
import { Check, EyeOff, FileText, Inbox, PanelRightOpen } from 'lucide-react';
import { cn } from '@/lib/utils';

interface IThreadDetailsHeaderProps {
  activeThread?: IThread;
  displayName?: string;
  visitorWebsite?: string | null;
  isSDRActive?: boolean;
  isJoining?: boolean;
  isLeaving?: boolean;
  onJoin?: () => void;
  onLeave?: () => void;
  presenceText?: string | null;
  onWhisper?: () => void;
  onSendForm?: () => void;
  isSendingForm?: boolean;
  isVisitorLive?: boolean;
  isConversationActive?: boolean;
  joinedBy?: string | null;
  isHumanRequested?: boolean;
  isSpam?: boolean;
  isDone?: boolean;
  isUpdatingDisposition?: boolean;
  onToggleDone?: () => void;
  onOpenContactPanel?: () => void;
}

const initialsFor = (name?: string) => {
  if (!name) return 'UV';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length > 1) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const ThreadDetailsHeader: FC<IThreadDetailsHeaderProps> = ({
  activeThread,
  displayName,
  visitorWebsite,
  isSDRActive = false,
  isJoining = false,
  isLeaving = false,
  onJoin,
  onLeave,
  onWhisper,
  onSendForm,
  isSendingForm = false,
  presenceText = null,
  isVisitorLive = false,
  isConversationActive = false,
  joinedBy = null,
  isHumanRequested = false,
  isSpam = false,
  isDone = false,
  isUpdatingDisposition = false,
  onToggleDone,
  onOpenContactPanel,
}) => {
  const lastActivityRelative = activeThread?.lastInteractionAt
    ? formatDistanceToNow(new Date(activeThread.lastInteractionAt), { addSuffix: true })
        .replace('about ', '')
        .replace('less than a minute ago', 'just now')
    : null;
  const domain = visitorWebsite?.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
  const statusLabel = isVisitorLive
    ? 'Active now'
    : (lastActivityRelative ? `Last seen ${lastActivityRelative}` : 'Last seen unknown');
  const teammateHandling = isConversationActive && !isSDRActive
    ? `${joinedBy || 'Teammate'} handling`
    : null;
  const meta = [statusLabel, isHumanRequested ? 'Human requested' : presenceText, teammateHandling, domain]
    .filter(Boolean)
    .join(' · ');

  return (
    <header className="flex h-[60px] flex-shrink-0 items-center gap-3 border-b border-slate-200 px-5">
      <div className="flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-full bg-[#EEF3FF] text-[12px] font-semibold text-[#0848E7]">
        {initialsFor(displayName)}
      </div>

      <div className="min-w-[40px] flex-1 overflow-hidden">
        <h2 className="truncate text-[14px] font-semibold leading-tight text-slate-900">
          {displayName || 'Unknown Visitor'}
        </h2>
        <p className={cn('mt-0.5 truncate text-[12px]', isVisitorLive ? 'text-emerald-600' : 'text-slate-400')}>{meta}</p>
      </div>

      {onSendForm && (
        <button
          type="button"
          onClick={onSendForm}
          disabled={isSendingForm || isSpam}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
          title="Send a form to the visitor"
          aria-label="Send a form to the visitor"
        >
          <FileText className="h-4 w-4" />
        </button>
      )}

      {onWhisper && (
        <button
          type="button"
          onClick={onWhisper}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
          title="Whisper to a teammate — the visitor won't see it"
          aria-label="Whisper to a teammate"
        >
          <EyeOff className="h-4 w-4" />
        </button>
      )}

      <div className="mx-1 h-5 w-px flex-shrink-0 bg-slate-200" />

      {!isSpam && onToggleDone && (
        <button
          type="button"
          onClick={onToggleDone}
          disabled={isUpdatingDisposition || isConversationActive}
          title={isConversationActive ? 'Leave the active chat before marking it done' : undefined}
          className="inline-flex h-8 flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-xl border border-slate-200 bg-white px-3 text-[13px] font-medium text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isDone ? <Inbox className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
          {isDone ? 'Move to inbox' : 'Mark done'}
        </button>
      )}

      {activeThread && !activeThread.isInitialThread && !isSpam && (
        <button
          type="button"
          onClick={isSDRActive ? onLeave : onJoin}
          disabled={isJoining || isLeaving || (isConversationActive && !isSDRActive)}
          title={isConversationActive && !isSDRActive ? `${joinedBy || 'A teammate'} is handling this conversation` : undefined}
          className={cn(
            'h-8 flex-shrink-0 whitespace-nowrap rounded-xl px-4 text-[13px] font-semibold transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50',
            isSDRActive ? 'bg-slate-100 text-slate-700' : 'bg-[#0848E7] text-white shadow-sm',
          )}
        >
          {isJoining ? 'Joining…' : isLeaving ? 'Leaving…' : isSDRActive ? 'Leave chat' : isConversationActive ? 'Teammate joined' : 'Join chat'}
        </button>
      )}

      {onOpenContactPanel && (
        <button
          type="button"
          onClick={onOpenContactPanel}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 md:hidden"
          aria-label="Open contact details"
          title="Open contact details"
        >
          <PanelRightOpen className="h-4 w-4" />
        </button>
      )}
    </header>
  );
};

export default ThreadDetailsHeader;
